import React from "react";
import { MusicHallStatusFormStyled } from "./styled";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import momentTZ from "moment-timezone";
import { BOOKING_EVENT_STATUS } from "utils/functions/musicHall";
import { AlertControl } from "components/Modals/Alert";
import { Buttons, Inputs } from "components";
import { MdClose } from "react-icons/md";
import { RenderCommaMoney } from "utils/functions/FormatMoney";

// คำอธิบายไทยของแต่ละสถานะ แสดงในตัวเลือกตอนกาง dropdown เพราะ label เป็นอังกฤษล้วน
// คนกรอกต้องเห็นความหมายตอนกำลังเลือก ไม่ใช่หลังเลือกเสร็จแล้ว
const EVENT_STATUS_DESCRIPTION = {
  1: "ยังไม่ถึงวันงาน",
  2: "งานจบแล้ว รอตรวจยอดเงินก่อนปิด",
  3: "งานจัดเรียบร้อย ปิดงานและบันทึกรายได้",
  4: "ลูกค้าไม่มาตามที่จอง ปิดงาน",
  5: "ยกเลิกก่อนวันงาน ไม่นับเป็นงาน",
};

// คำเตือนของสถานะที่ปิดแล้วย้อนไม่ได้ แยกออกมาเป็นตัวแดงเพื่อให้สะดุดตากว่าวงเล็บต่อท้าย
const EVENT_STATUS_WARNING = {
  3: "แก้ไขไม่ได้อีก",
  4: "แก้ไขไม่ได้อีก",
};

// 3 = Closed - Success, 4 = Closed - No Show ปิดแล้ว API จะไม่ให้แก้อีก (adminEventStatus)
const TERMINAL_EVENT_STATUS = ["3", "4"];

// >0 = ยังไม่ถึงวันงาน (อีกกี่วัน), 0 = วันนี้วันงาน, <0 = ผ่านวันงานมาแล้ว, null = ไม่รู้วันงาน
const getEventDayDiff = (booking_date) => {
  if (!booking_date) return null;
  const today = momentTZ().tz("Asia/Bangkok").startOf("day");
  const eventDay = momentTZ(booking_date).tz("Asia/Bangkok").startOf("day");
  return eventDay.diff(today, "days");
};

// กรองให้เหลือเฉพาะสถานะที่มีความหมายจริงในช่วงเวลานั้น จาก 5 ตัวเลือกเหลือ 2-4 ตัว
const getEventStatusOptions = (dayDiff, currentEventStatus) => {
  const values =
    dayDiff === null || dayDiff > 0
      ? ["1", "5"] // ก่อนถึงวันงาน ยังปิดงานไม่ได้
      : ["2", "3", "4", "5"]; // ถึง/ผ่านวันงานแล้ว
  // กันสถานะปัจจุบันหายไปจาก dropdown จนกลายเป็นค่าว่าง
  if (currentEventStatus && !values.includes(currentEventStatus)) {
    values.unshift(currentEventStatus);
  }
  return values
    .map((value) => BOOKING_EVENT_STATUS.find((e) => e.value === value))
    .filter((e) => e)
    .map((e) => ({
      value: e.value,
      // คำอธิบายซ่อนไว้ตอนอยู่ในช่องที่เลือกแล้ว (CSS) โชว์เฉพาะตอนกางรายการ
      label: (
        <span className="opt_label">
          <span className="opt_name">{e.label}</span>
          <span className="opt_desc">
            {EVENT_STATUS_DESCRIPTION[e.value]}
            {EVENT_STATUS_WARNING[e.value] && (
              <span className="opt_warn"> · {EVENT_STATUS_WARNING[e.value]}</span>
            )}
          </span>
        </span>
      ),
    }));
};

const getEventStatusSuggestion = (dayDiff) => {
  if (dayDiff === null) return "";
  if (dayDiff > 0) {
    return `ยังไม่ถึงวันจัดงาน (อีก ${dayDiff} วัน) → แนะนำ "Waiting for event"`;
  }
  if (dayDiff === 0) {
    return `วันนี้เป็นวันจัดงาน → เมื่องานจบแล้วแนะนำ "Checking before Close"`;
  }
  return `งานผ่านมาแล้ว ${Math.abs(
    dayDiff,
  )} วัน → แนะนำ "Closed - Success" ถ้างานจัดเรียบร้อย`;
};

const toNumber = (value) => Number(value) || 0;

const MusicHallStatusForm = ({
  initialValues,
  booking_date,
  bookingStatus,
  isInternal,
  onBack,
  onSubmit,
}) => {
  const schema = yup.object().shape({
    event_status: yup.string().required("required"),
    advance_booking_paid: yup.number().typeError("must be a number"),
  });

  // ค่าห้อง: ครั้งแรก (ยังไม่เคยบันทึก = 0) ดึงยอดจองล่วงหน้ามาให้ แอดมินแก้ได้
  // base_amount ไม่ถูกตั้งตอนสร้าง booking ค่าจึงเป็น 0 จนกว่าจะกรอกในฟอร์มนี้
  const defaultBaseAmount =
    toNumber(initialValues?.base_amount) > 0
      ? initialValues.base_amount
      : initialValues?.advance_booking_amount || "";

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { ...initialValues, base_amount: defaultBaseAmount },
  });

  // C = A + B (แสดงอย่างเดียว ไม่ส่งไป API) / ยอดคงค้าง = C - D
  const totalServiceAmount =
    toNumber(watch("base_amount")) +
    toNumber(watch("additional_services_amount"));
  const outstandingAmount =
    totalServiceAmount - toNumber(watch("advance_booking_paid"));

  const currentEventStatus = initialValues?.event_status;
  const isEventStatusLocked =
    TERMINAL_EVENT_STATUS.includes(currentEventStatus);
  const eventDayDiff = getEventDayDiff(booking_date);
  const eventStatusOptions = getEventStatusOptions(
    eventDayDiff,
    currentEventStatus,
  );
  const eventStatusSuggestion = getEventStatusSuggestion(eventDayDiff);

  // ปิดงานแล้วแก้ไม่ได้อีก ให้ยืนยันก่อน คนกรอกจะได้ไม่ต้องลังเลนานหน้า dropdown
  const _handleConfirmSubmit = (values) => {
    if (!TERMINAL_EVENT_STATUS.includes(values.event_status)) {
      onSubmit(values);
      return;
    }
    const statusLabel = BOOKING_EVENT_STATUS.find(
      (e) => e.value === values.event_status,
    )?.label;
    AlertControl.show({
      title: "ยืนยันปิดงาน",
      description: `เมื่อปิดงานเป็น "${statusLabel}" แล้ว จะไม่สามารถแก้ไขสถานะกิจกรรมหรือยอดเงินของการจองนี้ได้อีก ยืนยันหรือไม่`,
      btnLabel1: "ยกเลิก",
      btnLabel2: "ยืนยัน",
      onEvent2: () => onSubmit(values),
    });
  };

  return (
    <MusicHallStatusFormStyled>
      <form onSubmit={handleSubmit(_handleConfirmSubmit)}>
        <div className="f_header">
          <div className="f_tile">Event Status</div>
          <div className="f_close" onClick={onBack}>
            <MdClose size={22} />
          </div>
        </div>
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.Dropdown
                {...field}
                label={"สถานะกิจกรรม"}
                disabled={isEventStatusLocked}
                options={eventStatusOptions}
                // placeholder={t('test')}
                errors={errors.event_status?.message}
              />
            )}
            name="event_status"
            defaultValue=""
          />
          {isEventStatusLocked ? (
            <div className="status_locked">
              การจองนี้ปิดงานแล้ว ไม่สามารถเปลี่ยนสถานะกิจกรรมได้อีก
            </div>
          ) : (
            eventStatusSuggestion && (
              <div className="status_hint">{eventStatusSuggestion}</div>
            )
          )}
        </div>
        <div className="f_divider" />
        {/* Internal ไม่มีรายรับ/รายได้ API ไม่รับยอดเงิน (adminEventStatus) จึงไม่ให้กรอก กันกรอกแล้วหายเงียบ */}
        {isInternal ? (
          <div className="status_hint">
            การจองแบบ Internal ไม่มีค่าใช้จ่าย ยอดรับและรายได้ในเมนู Accounting
            เป็น 0
          </div>
        ) : (
          <>
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.TextField
                {...field}
                type="Number"
                label={"ค่าห้อง"}
                errors={errors.base_amount?.message}
              />
            )}
            name="base_amount"
            defaultValue=""
          />
        </div>
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.TextField
                {...field}
                type="Number"
                label={"ค่าบริการเพิ่มเติม"}
                errors={errors.additional_services_amount?.message}
              />
            )}
            name="additional_services_amount"
            defaultValue=""
          />
        </div>
        <div className="sum_row">
          <div className="sum_label">รวมค่าบริการ</div>
          <div className="sum_value">
            {RenderCommaMoney(totalServiceAmount)} บาท
          </div>
        </div>
        <div className="f_divider" />
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.TextField
                {...field}
                type="Number"
                label={"ยอดรวมชำระเงินจากลูกค้าทุกช่องทาง"}
                errors={errors.advance_booking_paid?.message}
              />
            )}
            name="advance_booking_paid"
            defaultValue=""
          />
          {/* จริงเฉพาะ status 4: Submit แล้ว API เปลี่ยนเป็น 5 ให้ (adminEventStatus) */}
          {bookingStatus === "4" && (
            <div className="paid_hint">
              กรุณากรอกยอดชำระเงิน เพื่อปรับสถานะเป็น Booking Success อัตโนมัติ
            </div>
          )}
        </div>
        <div className="sum_row outstanding">
          <div className="sum_label">ยอดคงค้าง</div>
          <div className="sum_value">
            {RenderCommaMoney(outstandingAmount)} บาท
            {outstandingAmount < 0 && (
              <span className="sum_note"> (ชำระเกิน)</span>
            )}
          </div>
        </div>
          </>
        )}
        <div className="input_wrap">
          <Controller
            control={control}
            render={({ field }) => (
              <Inputs.TextArea
                {...field}
                label={"Admin Note"}
                // placeholder={t('test')}
                errors={errors.additional_services_admin_note?.message}
              />
            )}
            name="additional_services_admin_note"
            defaultValue=""
          />
        </div>
        <div className="action_layout">
          <div className="btn_action_wrap">
            <Buttons.BgStandard theme_purple type="submit" label="Update" />
          </div>
        </div>
      </form>
    </MusicHallStatusFormStyled>
  );
};

MusicHallStatusForm.propTypes = {};

export default MusicHallStatusForm;
